import FormField from "./FormField";
import { useSiteContent } from "@/lib/siteContent";
import { formatCep } from "@/lib/commerce";
import type { CheckoutForm } from "@/types";
import type { CheckoutErrors } from "@/lib/commerce";

interface StepEnderecoProps {
  form: CheckoutForm;
  errors: CheckoutErrors;
  onChange: (name: string, value: string) => void;
  onAddress: (name: keyof CheckoutForm["address"], value: string) => void;
}

export default function StepEndereco({ form, errors, onChange, onAddress }: StepEnderecoProps) {
  const content = useSiteContent();
  const method = form.deliveryMethod
    ? content.delivery.find((item) => item.id === form.deliveryMethod)
    : undefined;
  const needsAddress = !method || Boolean(method.requiresAddress || method.requiresCep);

  if (!needsAddress) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="font-heading text-2xl text-foreground-900">Endereço</h2>
          <p className="mt-1 text-sm text-foreground-600">
            Para {method?.name.toLowerCase()}, não precisamos do endereço agora.
          </p>
        </div>
        <p className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4 text-sm leading-relaxed text-foreground-700">
          <i className="ri-information-line mt-0.5 text-lg text-secondary-700" />
          {form.deliveryMethod === "retirada"
            ? "Você escolheu retirar o produto. O local e o horário serão combinados com você no atendimento."
            : "A forma de recebimento selecionada não exige endereço. Seus dados já estão guardados e continuam salvos se você mudar de ideia."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-heading text-2xl text-foreground-900">Endereço</h2>
        <p className="mt-1 text-sm text-foreground-600">
          Preencha os dados de entrega. Você pode completar tudo manualmente, sem pressa.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[1fr_2fr]">
        <FormField
          label="CEP"
          name="cep"
          value={form.address.cep}
          onChange={(_, value) => onAddress("cep", formatCep(value))}
          error={errors.cep}
          placeholder="00000-000"
          inputMode="numeric"
          autoComplete="postal-code"
          hint={method?.requiresCep ? "Obrigatório para esta modalidade." : "Opcional aqui."}
        />
        <FormField
          label="Rua ou avenida"
          name="street"
          value={form.address.street}
          onChange={(_, value) => onAddress("street", value)}
          error={errors.street}
          placeholder="Nome da rua"
          autoComplete="address-line1"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[1fr_2fr]">
        <FormField
          label="Número"
          name="number"
          value={form.address.number}
          onChange={(_, value) => onAddress("number", value)}
          error={errors.number}
          placeholder="Ex.: 100"
        />
        <FormField
          label="Complemento"
          name="complement"
          value={form.address.complement}
          onChange={(_, value) => onAddress("complement", value)}
          placeholder="Apto., bloco, casa"
          optional
          autoComplete="address-line2"
        />
      </div>

      <FormField
        label="Bairro"
        name="district"
        value={form.address.district}
        onChange={(_, value) => onAddress("district", value)}
        error={errors.district}
        placeholder="Seu bairro"
        autoComplete="address-level3"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[2fr_1fr]">
        <FormField
          label="Cidade da entrega"
          name="addrCity"
          value={form.address.city}
          onChange={(_, value) => onAddress("city", value)}
          placeholder="Cidade"
          hint="Se for a mesma que você informou, é só repetir."
        />
        <FormField
          label="Estado"
          name="addrState"
          value={form.address.state}
          onChange={(_, value) => onAddress("state", value.toUpperCase().slice(0, 2))}
          placeholder="GO"
          maxLength={2}
        />
      </div>

      <FormField
        label="Ponto de referência"
        name="reference"
        value={form.address.reference}
        onChange={(_, value) => onAddress("reference", value)}
        placeholder="Ex.: próximo ao shopping"
        optional
      />

      {method?.notice ? (
        <p className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-3 text-[11px] leading-relaxed text-foreground-700">
          <i className="ri-information-line mt-0.5 text-base text-secondary-700" />
          {method.notice}
        </p>
      ) : null}
    </div>
  );
}